import PropTypes from "prop-types"
import React, { useState, useEffect, useRef, useCallback } from "react"
import { Modal } from "antd"
import Button from "components/ui/Button"
import { trackEvent } from "lib/googleTagManager"
import { isFreemailDomain } from "lib/freemailDomains"
import submitHubspotForm from "lib/submitHubspotForm"
import * as css from "./ContactSalesModal.module.css"

const FORM_ID = "bc9e3950-073c-4e5e-8a73-2612a46a3df8"

// Resolved once per page load — timezone doesn't change during a session
const VISITOR_TZ =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "America/Los_Angeles"

const EMPTY_FORM = {
  firstname: "",
  lastname: "",
  email: "",
  company: "",
  jobtitle: "",
  message: "",
}

function formatSlot(millis) {
  const d = new Date(millis)
  const day = d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: VISITOR_TZ,
  })
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: VISITOR_TZ,
  })
  return { day, time }
}

function SlotGrid({ slots, selectedSlot, onSelect }) {
  return (
    <div className={css.slotGrid}>
      {slots.map((slot) => {
        const { day, time } = formatSlot(slot.startMillisUtc)
        const isSelected = selectedSlot?.startMillisUtc === slot.startMillisUtc
        return (
          <button
            key={slot.startMillisUtc}
            type="button"
            className={`${css.slotChip} ${isSelected ? css.slotChipSelected : ""}`}
            onClick={() => onSelect(isSelected ? null : slot)}
          >
            <span className={css.slotDay}>{day}</span>
            <span className={css.slotTime}>{time}</span>
          </button>
        )
      })}
    </div>
  )
}

function ContactSalesModal({ isOpen, onClose, sourcePage = "", onSuccess }) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [bookingError, setBookingError] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [activeTab, setActiveTab] = useState("message")
  const [slotsState, setSlotsState] = useState({ status: "idle", slots: [] })
  const [formData, setFormData] = useState(EMPTY_FORM)

  const requireCompany = isFreemailDomain(formData.email)
  // Work email with a complete domain = eligible for lookup.
  // Requires at least 2 chars after the final dot (all real TLDs are min 2),
  // so evan@gmail. and evan@gmail.c don't trigger until TLD is complete.
  const emailDomain = formData.email.split("@")[1] ?? ""
  const domainParts = emailDomain.split(".")
  const tld = domainParts[domainParts.length - 1]
  const hasCompleteDomain =
    domainParts.length >= 2 && tld.length >= 2 && !requireCompany

  // Cookie-based lookup state (fires on modal open if hubspotutk is present)
  const [cookieLookup, setCookieLookup] = useState({
    status: "idle", // idle | loading | done
    found: false,
    inPipeline: false,
    rep: null,
  })

  // Company lookup state (fires when user types a complete work-email domain)
  const [domainLookup, setDomainLookup] = useState({
    status: "idle", // idle | loading | done
    inPipeline: false,
    rep: null,
  })

  // Lookup company when domain becomes complete; reset when it changes
  useEffect(() => {
    if (!hasCompleteDomain) {
      setDomainLookup({ status: "idle", inPipeline: false, rep: null })
      return
    }

    let cancelled = false

    // Debounce: wait 300ms after the last keystroke before firing
    const timer = setTimeout(() => {
      setDomainLookup({ status: "loading", inPipeline: false, rep: null })

      fetch("/.netlify/functions/lookup-company-domain", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: emailDomain }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (!cancelled) {
            setDomainLookup({
              status: "done",
              inPipeline: !!data.inPipeline,
              rep: data.rep ?? null,
            })
          }
        })
        .catch(() => {
          if (!cancelled) {
            // Fail open — show booking tab on error, default to Zane
            setDomainLookup({
              status: "done",
              inPipeline: false,
              rep: {
                name: "Zane",
                meetingSlug: "zane-aitken/introductory-call",
              },
            })
          }
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [emailDomain, hasCompleteDomain])

  // Either source can qualify; either source can block (pipeline wins)
  const cookieBlocked =
    cookieLookup.status === "done" && cookieLookup.inPipeline
  const domainBlocked =
    hasCompleteDomain &&
    domainLookup.status === "done" &&
    domainLookup.inPipeline
  const cookieQualified =
    cookieLookup.status === "done" &&
    cookieLookup.found &&
    !cookieLookup.inPipeline
  const domainQualified =
    hasCompleteDomain &&
    domainLookup.status === "done" &&
    !domainLookup.inPipeline
  const isQualified =
    !cookieBlocked && !domainBlocked && (cookieQualified || domainQualified)

  // Domain rep takes precedence (user typed it); fall back to cookie-resolved rep
  const effectiveRep = domainLookup.rep ?? cookieLookup.rep
  const effectiveRepSlug = isQualified
    ? (effectiveRep?.meetingSlug ?? null)
    : null

  // Drop back to message tab if no longer qualified
  useEffect(() => {
    if (!isQualified) {
      setActiveTab("message")
      setSlotsState({ status: "idle", slots: [] })
    }
  }, [isQualified])

  // Fetch real meeting slots when a rep is known
  useEffect(() => {
    const meetingSlug = effectiveRepSlug
    if (!meetingSlug) return

    let cancelled = false
    setSlotsState({ status: "loading", slots: [] })

    const timezone = VISITOR_TZ

    fetch("/.netlify/functions/get-meeting-slots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: meetingSlug, timezone }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) {
          setSlotsState({ status: "done", slots: data.slots ?? [] })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSlotsState({ status: "done", slots: [] })
        }
      })

    return () => {
      cancelled = true
    }
  }, [effectiveRepSlug])

  // Scroll shadow state — tracks whether content is hidden above/below
  const scrollRef = useRef(null)
  const [topShadow, setTopShadow] = useState(false)
  const [bottomShadow, setBottomShadow] = useState(false)

  const updateShadows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setTopShadow(el.scrollTop > 2)
    setBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight - 2)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateShadows()
    el.addEventListener("scroll", updateShadows, { passive: true })
    // Re-check when content height changes (e.g. freemail fields appear)
    const ro = new ResizeObserver(updateShadows)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", updateShadows)
      ro.disconnect()
    }
  }, [updateShadows, isOpen])

  // Reset form and track view when modal opens; kick off cookie-based lookup
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false)
      setError(null)
      setBookingError(null)
      setSelectedSlot(null)
      setActiveTab("message")
      setSlotsState({ status: "idle", slots: [] })
      setFormData(EMPTY_FORM)
      setDomainLookup({ status: "idle", inPipeline: false, rep: null })

      // Look up visitor by HubSpot tracking cookie immediately
      const utk =
        typeof document !== "undefined"
          ? document.cookie
              .split("; ")
              .find((c) => c.startsWith("hubspotutk="))
              ?.split("=")[1]
          : null

      if (utk) {
        setCookieLookup({
          status: "loading",
          found: false,
          inPipeline: false,
          rep: null,
        })
        fetch("/.netlify/functions/lookup-contact-utk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ utk }),
        })
          .then((r) => r.json())
          .then((data) => {
            setCookieLookup({
              status: "done",
              found: !!data.found,
              inPipeline: !!data.inPipeline,
              rep: data.rep ?? null,
            })
          })
          .catch(() => {
            setCookieLookup({
              status: "done",
              found: false,
              inPipeline: false,
              rep: null,
            })
          })
      } else {
        setCookieLookup({
          status: "done",
          found: false,
          inPipeline: false,
          rep: null,
        })
      }

      trackEvent("contactSalesFormViewed", {
        formId: FORM_ID,
        sourcePage,
        pageSlug: typeof window !== "undefined" ? window.location.pathname : "",
        pageName: typeof document !== "undefined" ? document.title : "",
      })
    }
  }, [isOpen, sourcePage])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setBookingError(null)

    try {
      const data = { ...formData, source_page: sourcePage }
      if (!requireCompany) {
        delete data.company
        delete data.jobtitle
      }

      // Form submission is the required step
      const formResponse = await submitHubspotForm(FORM_ID, data)

      if (!formResponse.ok) {
        setError("We couldn't send your message — please try again.")
        return
      }

      // Form succeeded — now independently await booking
      let bookingOk = !selectedSlot || !effectiveRepSlug
      if (selectedSlot && effectiveRepSlug) {
        const timezone =
          typeof Intl !== "undefined"
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : "UTC"
        try {
          const bookRes = await fetch("/.netlify/functions/book-meeting", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slug: effectiveRepSlug,
              startTime: selectedSlot.startMillisUtc,
              duration: 1800000,
              firstName: formData.firstname,
              lastName: formData.lastname,
              email: formData.email,
              timezone,
            }),
          })
          const bookData = await bookRes.json().catch(() => ({}))
          bookingOk = !!bookData.booked
        } catch {
          bookingOk = false
        }
        if (!bookingOk) {
          setBookingError(true)
        }
      }

      setSubmitted(true)
      trackEvent("formSubmission", {
        formSubmissionEventName: "Contact Sales Modal",
        sourcePage,
        meetingBooked: !!selectedSlot && bookingOk,
      })

      if (onSuccess) {
        try {
          const maybePromise = onSuccess()
          if (maybePromise && typeof maybePromise.then === "function") {
            await maybePromise
          }
        } catch (_callbackErr) {
          // Swallow callback errors so they don't change the submission UX
        }
      }
    } catch (_err) {
      setError("We couldn't send your message — please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={520}
      centered
      destroyOnClose
      className={css.modal}
      styles={{
        body: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        },
      }}
    >
      <div className={css.content}>
        <div className={css.header}>
          <span className={css.label}>Contact Preset</span>
          <h2 className={css.title}>Talk to the Preset team</h2>
          {!submitted && (
            <p className={css.description}>
              Ready to learn more? Please fill out the form and we&apos;ll be in
              touch.
            </p>
          )}
        </div>

        {submitted ? (
          <div className={css.thankYou}>
            <svg
              className={css.checkIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <div>
              <p>
                Thank you for reaching out! We will be in touch shortly. Please
                keep an eye out for emails from &quot;@preset.io&quot;
              </p>
              {bookingError && (
                <p className={css.bookingError}>
                  We weren&apos;t able to book the time slot automatically.{" "}
                  {effectiveRepSlug ? (
                    <a
                      href={`https://meetings.hubspot.com/${effectiveRepSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css.bookingErrorLink}
                    >
                      Book directly here
                    </a>
                  ) : (
                    "Please reply to your confirmation email to schedule a time."
                  )}
                </p>
              )}
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={`${css.form}${topShadow ? ` ${css.shadowTop}` : ""}${bottomShadow ? ` ${css.shadowBottom}` : ""}`}
          >
            <div ref={scrollRef} className={css.formScroll}>
              <div className={css.nameRow}>
                <div className={css.field}>
                  <label htmlFor="modal-firstname" className={css.fieldLabel}>
                    First Name <span className={css.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-firstname"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    required
                    className={css.input}
                  />
                </div>

                <div className={css.field}>
                  <label htmlFor="modal-lastname" className={css.fieldLabel}>
                    Last Name <span className={css.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-lastname"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    required
                    className={css.input}
                  />
                </div>
              </div>

              <div className={css.field}>
                <label htmlFor="modal-email" className={css.fieldLabel}>
                  Email <span className={css.required}>*</span>
                  {domainLookup.status === "loading" && (
                    <span className={css.lookupSpinner} aria-hidden="true" />
                  )}
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={css.input}
                />
              </div>

              {requireCompany && (
                <div className={css.freemailFields}>
                  <p className={css.freemailNote}>
                    Please tell us a bit about your company so we can better
                    assist you.
                  </p>
                  <div className={css.field}>
                    <label htmlFor="modal-company" className={css.fieldLabel}>
                      Company Name <span className={css.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="modal-company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required={requireCompany}
                      className={css.input}
                    />
                  </div>
                  <div className={css.field}>
                    <label htmlFor="modal-jobtitle" className={css.fieldLabel}>
                      Job Title <span className={css.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="modal-jobtitle"
                      name="jobtitle"
                      value={formData.jobtitle}
                      onChange={handleChange}
                      required={requireCompany}
                      className={css.input}
                    />
                  </div>
                </div>
              )}

              {/* Message / Book a Call tabs */}
              <div className={css.field}>
                <div className={css.tabBar}>
                  <button
                    type="button"
                    className={`${css.tab} ${activeTab === "message" ? css.tabActive : ""}`}
                    onClick={() => setActiveTab("message")}
                  >
                    Message
                  </button>
                  {isQualified && (
                    <button
                      type="button"
                      className={`${css.tab} ${activeTab === "book" ? css.tabActive : ""}`}
                      onClick={() => setActiveTab("book")}
                    >
                      Book a Call{" "}
                      <span className={css.tabOptional}>(optional)</span>
                    </button>
                  )}
                </div>

                {activeTab === "message" ? (
                  <>
                    <textarea
                      id="modal-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={css.textarea}
                      placeholder="Tell us about your project or questions..."
                    />
                    {selectedSlot && (
                      <div className={css.slotConfirm}>
                        <span className={css.slotConfirmText}>
                          📅 {(() => {
                            const { day, time } = formatSlot(
                              selectedSlot.startMillisUtc
                            )
                            return `${day} · ${time}`
                          })()}
                        </span>
                        <button
                          type="button"
                          className={css.slotConfirmChange}
                          onClick={() => setActiveTab("book")}
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          className={css.slotConfirmRemove}
                          onClick={() => setSelectedSlot(null)}
                          aria-label="Remove selected time"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={css.slotPanel}>
                    <p className={css.slotHint}>
                      30-min intro call
                      {effectiveRep ? ` with ${effectiveRep.name}` : ""}
                      {" · "}select a time below
                    </p>
                    {slotsState.status === "loading" ? (
                      <div className={css.slotGridSkeleton}>
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className={css.slotChipSkeleton} />
                        ))}
                      </div>
                    ) : slotsState.slots.length === 0 ? (
                      <p className={css.slotEmpty}>
                        No available times right now — check back soon.
                      </p>
                    ) : (
                      <SlotGrid
                        slots={slotsState.slots}
                        selectedSlot={selectedSlot}
                        onSelect={setSelectedSlot}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={css.formFooter}>
              {error && <p className={css.error}>{error}</p>}
              <Button
                type="submit"
                color="lush"
                size="medium"
                disabled={submitting}
                className={css.submitButton}
              >
                {submitting
                  ? "Sending..."
                  : selectedSlot
                    ? `Book ${formatSlot(selectedSlot.startMillisUtc).time} & Send Message`
                    : "Submit"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}

SlotGrid.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({ startMillisUtc: PropTypes.number })
  ).isRequired,
  selectedSlot: PropTypes.shape({ startMillisUtc: PropTypes.number }),
  onSelect: PropTypes.func.isRequired,
}

ContactSalesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourcePage: PropTypes.string,
  onSuccess: PropTypes.func,
}

export default ContactSalesModal
